import React, { useState, useEffect } from 'react';
import { Edit, Trash2, X, Plus, ArrowLeft, ArrowRight } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import Table from "../../base-component/Table/Table";
import Pagination from "../../base-component/Pagination/Pagination";
import Dialog from "../../base-component/Dialog/Dialog";
import { getCurrencyListApi } from '../../Services/ApiServices';
import { updateCurrencyApi } from '../../Services/ApiServices';
import { addCurrencyApi } from '../../Services/ApiServices';
import { deleteCurrencyApi } from '../../Services/ApiServices';
import toast from 'react-hot-toast';

const Currency = () => {
  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [currencies, setCurrencies] = useState([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [editedCurrencyName, setEditedCurrencyName] = useState("");
  const [newCurrencyName, setNewCurrencyName] = useState(""); 

  const handleEdit = (currency) => {
    setSelectedCurrency(currency);
    setEditedCurrencyName(currency.currency || currency.name);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (currency) => {
    setSelectedCurrency(currency);
    setIsDeleteDialogOpen(true);
  };

  const handleEditConfirm = (values) => {
    if (selectedCurrency && values.name.trim()) {
      let params = {
        currency_id: selectedCurrency.id || selectedCurrency.currency_id,
        currency: values.name.trim()
      };
      updateCurrencyApi(params)
        .then(res => {
          toast.success("Currency updated successfully");
          fetchCurrencies();
          setIsEditDialogOpen(false);
          setSelectedCurrency(null);
          setEditedCurrencyName("");
        })
        .catch(err => {
          console.log(err);
          toast.error(err?.response?.data?.message || "Failed to update currency");
        });
    } else {
      setIsEditDialogOpen(false);
      setSelectedCurrency(null);
      setEditedCurrencyName("");
    }
  };

  const handleDeleteConfirm = () => {
    if (!selectedCurrency) return;

    let params = {
      currency_id: selectedCurrency.id || selectedCurrency.currency_id
    };

    deleteCurrencyApi(params)
      .then(res => {
        toast.success("Currency deleted successfully");
        setIsDeleteDialogOpen(false);
        setSelectedCurrency(null);
        fetchCurrencies();
      })
      .catch(err => {
        console.log(err);
        toast.error(err?.response?.data?.message || "Failed to delete currency");
      });
  };

  const handleAddCurrency = () => {
    setNewCurrencyName("");
    setIsAddDialogOpen(true);
  };

  const handleAddConfirm = (values) => {
    if (values.name.trim()) {
      let params = {
        currency: values.name.trim()
      };
      addCurrencyApi(params)
        .then(res => {
          toast.success("Currency added successfully");
          fetchCurrencies();
          setIsAddDialogOpen(false);
          setNewCurrencyName("");
        })
        .catch(err => {
          console.log(err);
          toast.error(err?.response?.data?.message || "Failed to add currency");
        });
    } else {
      setIsAddDialogOpen(false);
      setNewCurrencyName("");
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters")
      .trim(),
  });

  const fetchCurrencies = () => {
    let params = {
      page: pageNo
    };
    getCurrencyListApi(params)
      .then(res => {
        setCurrencies(res.data.data);
        setPageCount(res.data.totalPages);
      })
      .catch(err => {
        console.log(err);
        toast.error(err.response.data.message);
      });
  };

  useEffect(() => {
    fetchCurrencies();
  }, [pageNo]);

  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const renderPaginationButtons = () => {
    const maxPagesToShow = 3;
    const buttons = [];
    const startPage = Math.max(1, pageNo - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(pageCount, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={
            i === pageNo
              ? "bg-[#2C5BA7] text-white w-10 h-10 rounded-lg font-medium text-xl"
              : "text-[#4B4B4B] w-10 h-10 rounded-lg font-medium text-xl"
          }
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <p className="text-xl p-0 m-0 font-bold text-gray-900 tracking-tight">Currency</p>

        <button
          onClick={handleAddCurrency}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#2C5BA7] text-white rounded-lg hover:bg-[#1e4a8a] transition-colors font-medium"
        >
          <Plus size={18} />
          Add Currency
        </button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <Table>
          <Table.Thead variant="light">
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>CURRENCY NAME</Table.Th>
              <Table.Th>CREATED AT</Table.Th>
              <Table.Th className="text-center">ACTIONS</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {currencies.map((currency, index) => (
              <Table.Tr key={currency.id}>
                <Table.Td className="font-semibold text-slate-700">
                  {(pageNo - 1) * 10 + index + 1}
                </Table.Td>

                <Table.Td className="text-slate-600 font-medium">
                  {currency.currency || currency.name}
                </Table.Td>

                <Table.Td className="text-slate-600">{moment(currency.created_at).format("DD-MM-YYYY h:mm A") || "—"}</Table.Td>

                <Table.Td>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => handleEdit(currency)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#10B981',
                        backgroundColor: '#D1FAE5',
                        color: '#10B981'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#059669';
                        e.target.style.backgroundColor = '#A7F3D0';
                        e.target.style.color = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#10B981';
                        e.target.style.backgroundColor = '#D1FAE5';
                        e.target.style.color = '#10B981';
                      }}
                    >
                      <Edit size={16} />
                    </button>

                    <button 
                      onClick={() => handleDelete(currency)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#F87171',
                        backgroundColor: '#FEF2F2',
                        color: '#F87171'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#ef4444';
                        e.target.style.backgroundColor = '#FEE2E2';
                        e.target.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#F87171';
                        e.target.style.backgroundColor = '#FEF2F2';
                        e.target.style.color = '#F87171';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>

      {currencies.length > 0 && (
        <div className="flex mt-5 items-center col-span-12 justify-between">
          <Pagination>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo - 1)}
                disabled={pageNo == 1}
              >
                <ArrowLeft />
              </button>
            </Pagination.Link>
            <div className="flex items-center">
              {renderPaginationButtons()}
            </div>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo + 1)}
                disabled={pageNo === pageCount}
              >
                <ArrowRight />
              </button>
            </Pagination.Link>
          </Pagination>
        </div>
      )}

      <Dialog
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedCurrency(null);
          setEditedCurrencyName("");
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="bg-white px-6 py-8 w-full relative">
              <button
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedCurrency(null);
                  setEditedCurrencyName("");
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-6">
                Edit Currency
              </div>

              <Formik
                initialValues={{ name: editedCurrencyName }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => handleEditConfirm(values)}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        Currency Name
                      </label>
                      <Field
                        type="text"
                        name="name"
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5BA7] focus:border-transparent ${
                          errors.name && touched.name
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter currency name"
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 h-12 shadow cursor-pointer bg-[#2C5BA7] text-white font-medium rounded-xl hover:bg-[#1e4a8a] transition-colors"
                    >
                      Update
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>

      <Dialog
        open={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setNewCurrencyName("");
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="bg-white px-6 py-8 w-full relative">
              <button
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setNewCurrencyName("");
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-6">
                Add Currency
              </div>

              <Formik
                initialValues={{ name: newCurrencyName }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  handleAddConfirm(values);
                  setNewCurrencyName("");
                }}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        Currency Name
                      </label>
                      <Field
                        type="text"
                        name="name"
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5BA7] focus:border-transparent ${
                          errors.name && touched.name
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter currency name"
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 h-12 shadow cursor-pointer bg-[#2C5BA7] text-white font-medium rounded-xl hover:bg-[#1e4a8a] transition-colors"
                    >
                      Add
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedCurrency(null);
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="px-4 py-4 text-center relative" style={{ backgroundColor: '#FEF2F2' }}>
              <button
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setSelectedCurrency(null);
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center">
                  <Trash2 size={56} className="text-[#F87171]" />
                </div>
              </div>
            </div>

            <div className="bg-white px-6 py-8 text-center w-full">
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-4">
                Delete Currency?
              </div>

              <Dialog.Description className="md:text-base text-sm text-gray-600 mb-6 leading-relaxed">
                Are you sure you want to delete <span className="font-semibold text-gray-900">{selectedCurrency?.currency || selectedCurrency?.name}</span>? This action cannot be undone.
              </Dialog.Description>

              <button
                onClick={handleDeleteConfirm}
                className="w-full px-6 h-12 shadow cursor-pointer bg-[#F87171] text-white font-medium rounded-xl hover:bg-[#ef4444] transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>
    </>
  );
};

export default Currency;
